import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Network } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useConnectionStore } from '@/stores/connection-store';
import { useTunnelStore } from '@/stores/tunnel-store';
import { useMobileStore } from '@/stores/mobile-store';
import { useToastStore } from '@/stores/toast-store';
import { useModalStore } from '@/stores/modal-store';
import { useDialogStore } from '@/stores/dialog-store';
import { useTapHandler } from '@/hooks/useTapHandler';
import { MobileActionSheet } from '../MobileActionSheet';
import { ConnectionModal } from '@/components/modals/ConnectionModal';
import { TunnelModal } from '@/components/modals/TunnelModal';
import { buildConnectionActions } from '@/lib/actions/connection-actions';
import { buildTunnelActions } from '@/lib/actions/tunnel-actions';
import type { ContextMenuItem } from '@/components/shared/ContextMenu';
import type { ConnectionConfig, TunnelConfig, ConnectionStatus } from '@/types';

export function ConnectionListScreen() {
  const pushScreen = useMobileStore((s) => s.pushScreen);
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const setStatus = useConnectionStore((s) => s.setStatus);
  const activeTunnels = useTunnelStore((s) => s.activeTunnels);
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();

  const [actionSheet, setActionSheet] = useState<{ items: ContextMenuItem[] } | null>(null);
  // Connection/tunnel modal state is shared with desktop via useModalStore.
  const connectionModal = useModalStore((s) => s.connectionModal);
  const tunnelModal = useModalStore((s) => s.tunnelModal);
  const openNewConnection = useModalStore((s) => s.openNewConnection);
  const openEditConnection = useModalStore((s) => s.openEditConnection);
  const closeConnectionModal = useModalStore((s) => s.closeConnectionModal);
  const openNewTunnel = useModalStore((s) => s.openNewTunnel);
  const openEditTunnel = useModalStore((s) => s.openEditTunnel);
  const closeTunnelModal = useModalStore((s) => s.closeTunnelModal);

  const { data: connections = [], isLoading: connectionsLoading } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
  });

  const { data: tunnels = [] } = useQuery<TunnelConfig[]>({
    queryKey: ['tunnels'],
    queryFn: () => apiClient.get<TunnelConfig[]>('/api/tunnels'),
  });

  const connectMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.post<{ ok: boolean }>(`/api/connections/${connectionId}/connect`),
    onMutate: (connectionId) => {
      setStatus(connectionId, 'connecting');
    },
    onSuccess: (_data, connectionId) => {
      setStatus(connectionId, 'connected');
      queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
    },
    onError: (err, connectionId) => {
      const message = err instanceof Error ? err.message : 'Connection failed';
      setStatus(connectionId, 'error', message);
      addToast('error', message);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.post<{ ok: boolean }>(`/api/connections/${connectionId}/disconnect`),
    onSuccess: (_data, connectionId) => {
      setStatus(connectionId, 'disconnected');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Disconnect failed';
      addToast('error', message);
    },
  });

  const deleteConnectionMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/connections/${connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      addToast('success', 'Connection deleted');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Delete failed';
      addToast('error', message);
    },
  });

  const deleteTunnelMutation = useMutation({
    mutationFn: (tunnelId: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/tunnels/${tunnelId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tunnels'] });
      addToast('success', 'Tunnel deleted');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Delete failed';
      addToast('error', message);
    },
  });

  const handleConnectionTap = useCallback(
    (conn: ConnectionConfig) => {
      const status: ConnectionStatus = activeConnections[conn.id]?.status ?? 'disconnected';

      if (status === 'connecting') return;

      if (status !== 'connected') {
        // Auto-connect then navigate
        setStatus(conn.id, 'connecting');
        apiClient
          .post<{ ok: boolean }>(`/api/connections/${conn.id}/connect`)
          .then(() => {
            setStatus(conn.id, 'connected');
            queryClient.invalidateQueries({ queryKey: ['schema', conn.id] });
            pushScreen({
              type: 'schema-browser',
              title: conn.name,
              params: { connectionId: conn.id, connectionName: conn.name },
            });
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Connection failed';
            setStatus(conn.id, 'error', message);
            addToast('error', message);
          });
      } else {
        pushScreen({
          type: 'schema-browser',
          title: conn.name,
          params: { connectionId: conn.id, connectionName: conn.name },
        });
      }
    },
    [activeConnections, setStatus, pushScreen, queryClient, addToast],
  );

  const handleConnectionLongPress = useCallback(
    (conn: ConnectionConfig) => {
      const status: ConnectionStatus = activeConnections[conn.id]?.status ?? 'disconnected';
      const items = buildConnectionActions(conn, status, {
        onConnect: () => connectMutation.mutate(conn.id),
        onDisconnect: () => disconnectMutation.mutate(conn.id),
        onNewQuery: () => {
          // Mobile doesn't have a query editor tab in-place; push to Query screen instead.
          useMobileStore.getState().setActiveTab('query');
        },
        onReload: () => {
          queryClient.invalidateQueries({ queryKey: ['schema', conn.id] });
        },
        onCreateDatabase: () => {
          addToast('info', 'Use desktop to create databases.');
        },
        onEdit: () => openEditConnection(conn),
        onRename: async () => {
          const newName = await useDialogStore.getState().showPrompt({
            title: 'Rename Connection',
            defaultValue: conn.name,
          });
          if (newName?.trim() && newName.trim() !== conn.name) {
            try {
              await apiClient.put(`/api/connections/${conn.id}`, { name: newName.trim() });
              queryClient.invalidateQueries({ queryKey: ['connections'] });
            } catch (err: unknown) {
              addToast('error', err instanceof Error ? err.message : 'Rename failed');
            }
          }
        },
        onCopyHostPort: () => {
          navigator.clipboard.writeText(`${conn.host}:${conn.port}`);
          addToast('success', 'Copied to clipboard');
        },
        onShare: () => useModalStore.getState().openShareDialog({
          resourceType: 'connection',
          resourceId: conn.id,
          resourceName: conn.name,
        }),
        onDelete: () => {
          useDialogStore.getState().showConfirm({
            title: 'Delete Connection',
            message: `This will permanently delete the connection "${conn.name}" and all of its saved queries. The remote database itself is untouched. This action cannot be undone.`,
            confirmText: conn.name,
            danger: true,
            onConfirm: async () => {
              await new Promise<void>((resolve, reject) => {
                deleteConnectionMutation.mutate(conn.id, {
                  onSuccess: () => resolve(),
                  onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
                });
              });
            },
          });
        },
      });
      setActionSheet({ items });
    },
    [
      activeConnections, connectMutation, disconnectMutation, deleteConnectionMutation,
      queryClient, addToast, openEditConnection,
    ],
  );

  const handleTunnelTap = useCallback(
    (tunnel: TunnelConfig) => {
      pushScreen({
        type: 'terminal-list',
        title: tunnel.name,
        params: { tunnelId: tunnel.id, tunnelName: tunnel.name },
      });
    },
    [pushScreen],
  );

  const handleTunnelLongPress = useCallback(
    (tunnel: TunnelConfig) => {
      const status = activeTunnels[tunnel.id]?.status ?? 'stopped';
      const sshCmd = `ssh -L ${tunnel.localPort ?? '?'}:localhost:${tunnel.port} ${tunnel.username}@${tunnel.host}`;
      const items = buildTunnelActions(tunnel, status, {
        onStart: async () => {
          try {
            await apiClient.post(`/api/tunnels/${tunnel.id}/start`);
            queryClient.invalidateQueries({ queryKey: ['tunnels'] });
          } catch (err: unknown) {
            addToast('error', err instanceof Error ? err.message : 'Failed to start tunnel');
          }
        },
        onStop: async () => {
          try {
            await apiClient.post(`/api/tunnels/${tunnel.id}/stop`);
            queryClient.invalidateQueries({ queryKey: ['tunnels'] });
          } catch (err: unknown) {
            addToast('error', err instanceof Error ? err.message : 'Failed to stop tunnel');
          }
        },
        onOpenTerminal: () => {
          pushScreen({
            type: 'terminal-view',
            title: `${tunnel.name} — Terminal`,
            params: { tunnelId: tunnel.id, tunnelName: tunnel.name },
          });
        },
        onCopySSHCommand: () => {
          navigator.clipboard.writeText(sshCmd);
          addToast('success', 'Copied SSH command');
        },
        onEdit: () => openEditTunnel(tunnel),
        onShare: () => useModalStore.getState().openShareDialog({
          resourceType: 'tunnel',
          resourceId: tunnel.id,
          resourceName: tunnel.name,
        }),
        onDelete: () => {
          useDialogStore.getState().showConfirm({
            title: 'Delete Tunnel',
            message: `This will permanently delete the SSH tunnel "${tunnel.name}". This action cannot be undone.`,
            confirmText: tunnel.name,
            danger: true,
            onConfirm: async () => {
              await new Promise<void>((resolve, reject) => {
                deleteTunnelMutation.mutate(tunnel.id, {
                  onSuccess: () => resolve(),
                  onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
                });
              });
            },
          });
        },
      }, activeTunnels[tunnel.id]?.localPort);
      setActionSheet({ items });
    },
    [activeTunnels, deleteTunnelMutation, queryClient, addToast, openEditTunnel, pushScreen],
  );

  const handleFabPress = useCallback(() => {
    setActionSheet({
      items: [
        {
          label: 'New Connection',
          icon: Plus,
          onClick: openNewConnection,
        },
        {
          label: 'New SSH Tunnel',
          icon: Network,
          onClick: openNewTunnel,
        },
      ],
    });
  }, [openNewConnection, openNewTunnel]);

  const driverLabel = (type: string): string => {
    const labels: Record<string, string> = {
      postgresql: 'PostgreSQL',
      mysql: 'MySQL',
      mssql: 'SQL Server',
      oracle: 'Oracle',
      sqlite: 'SQLite',
      mongodb: 'MongoDB',
      files: 'Files',
    };
    return labels[type] ?? type;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        {/* Refresh button */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <span className="text-xs font-medium text-text-muted uppercase tracking-wide">
            Connections
          </span>
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['connections'] });
              queryClient.invalidateQueries({ queryKey: ['tunnels'] });
            }}
            className="p-1 text-text-muted active:text-accent-blue"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Connections list */}
        <div className="px-4 pb-2">
          {connectionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-lg animate-pulse"
                  style={{ height: 64, backgroundColor: 'var(--dbgo-panel)' }}
                />
              ))}
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-8 text-text-muted text-sm">
              No connections yet
            </div>
          ) : (
            <div className="space-y-2">
              {connections.map((conn) => {
                const status: ConnectionStatus =
                  activeConnections[conn.id]?.status ?? 'disconnected';
                const isConnected = status === 'connected';
                const isConnecting = status === 'connecting';

                return (
                  <ConnectionRow
                    key={conn.id}
                    name={conn.name}
                    subtitle={`${driverLabel(conn.driverType)} \u00B7 ${conn.host}`}
                    isConnected={isConnected}
                    isConnecting={isConnecting}
                    colorTag={conn.colorTag}
                    onTap={() => handleConnectionTap(conn)}
                    onLongPress={() => handleConnectionLongPress(conn)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Tunnels section */}
        {tunnels.length > 0 && (
          <>
            <div className="flex items-center gap-3 px-4 py-2">
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-default)' }} />
              <span className="text-xs font-medium text-text-muted uppercase tracking-wide">
                Tunnels
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-default)' }} />
            </div>

            <div className="px-4 pb-4 space-y-2">
              {tunnels.map((tunnel) => {
                const tunnelStatus = activeTunnels[tunnel.id]?.status ?? 'stopped';
                const isRunning = tunnelStatus === 'running';

                return (
                  <TunnelRow
                    key={tunnel.id}
                    name={tunnel.name}
                    subtitle={`${tunnel.host}:${tunnel.port}`}
                    isRunning={isRunning}
                    onTap={() => handleTunnelTap(tunnel)}
                    onLongPress={() => handleTunnelLongPress(tunnel)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={handleFabPress}
        className="absolute right-4 bottom-20 flex items-center justify-center rounded-full shadow-lg active:scale-95 transition-transform"
        style={{
          width: 52,
          height: 52,
          backgroundColor: 'var(--accent-blue)',
        }}
      >
        <Plus size={24} className="text-white" />
      </button>

      {/* Action sheet */}
      {actionSheet && (
        <MobileActionSheet
          items={actionSheet.items}
          onClose={() => setActionSheet(null)}
        />
      )}

      {/* Connection modal — new or edit (state in useModalStore) */}
      {connectionModal.mode !== 'closed' && (
        <ConnectionModal
          connectionId={connectionModal.mode === 'edit' ? connectionModal.connection.id : undefined}
          initialData={connectionModal.mode === 'edit' ? connectionModal.connection : undefined}
          onClose={closeConnectionModal}
          onSaved={() => {
            closeConnectionModal();
            queryClient.invalidateQueries({ queryKey: ['connections'] });
          }}
        />
      )}

      {/* Tunnel modal — new or edit (state in useModalStore) */}
      {tunnelModal.mode !== 'closed' && (
        <TunnelModal
          tunnelId={tunnelModal.mode === 'edit' ? tunnelModal.tunnel.id : undefined}
          initialData={tunnelModal.mode === 'edit' ? tunnelModal.tunnel : undefined}
          onClose={closeTunnelModal}
          onSaved={() => {
            closeTunnelModal();
            queryClient.invalidateQueries({ queryKey: ['tunnels'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Connection Row ──────────────────────────────────────

interface ConnectionRowProps {
  name: string;
  subtitle: string;
  isConnected: boolean;
  isConnecting: boolean;
  colorTag: string | null;
  onTap: () => void;
  onLongPress: () => void;
}

function ConnectionRow({
  name,
  subtitle,
  isConnected,
  isConnecting,
  colorTag,
  onTap,
  onLongPress,
}: ConnectionRowProps) {
  const tapHandlers = useTapHandler({ onTap, onLongPress });

  const dotColor = isConnecting
    ? 'var(--accent-yellow)'
    : isConnected
      ? 'var(--accent-green)'
      : 'var(--text-muted)';

  return (
    <button
      className="w-full flex items-center gap-3 rounded-lg p-3 text-left active:opacity-70 transition-opacity"
      style={{
        backgroundColor: 'var(--dbgo-panel)',
        borderLeft: colorTag ? `3px solid ${colorTag}` : undefined,
      }}
      {...tapHandlers}
    >
      <div
        className="rounded-full shrink-0"
        style={{
          width: 10,
          height: 10,
          backgroundColor: dotColor,
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-text-primary truncate">
          {name}
        </div>
        <div className="text-xs text-text-secondary truncate">
          {subtitle}
        </div>
      </div>
      {isConnecting && (
        <div className="shrink-0">
          <RefreshCw size={14} className="text-text-muted animate-spin" />
        </div>
      )}
    </button>
  );
}

// ─── Tunnel Row ──────────────────────────────────────

interface TunnelRowProps {
  name: string;
  subtitle: string;
  isRunning: boolean;
  onTap: () => void;
  onLongPress: () => void;
}

function TunnelRow({ name, subtitle, isRunning, onTap, onLongPress }: TunnelRowProps) {
  const tapHandlers = useTapHandler({ onTap, onLongPress });

  return (
    <button
      className="w-full flex items-center gap-3 rounded-lg p-3 text-left active:opacity-70 transition-opacity"
      style={{ backgroundColor: 'var(--dbgo-panel)' }}
      {...tapHandlers}
    >
      <div
        className="rounded-full shrink-0"
        style={{
          width: 10,
          height: 10,
          backgroundColor: isRunning ? 'var(--accent-green)' : 'var(--text-muted)',
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-text-primary truncate">
          {name}
        </div>
        <div className="text-xs text-text-secondary truncate">
          {subtitle}
        </div>
      </div>
    </button>
  );
}

